/**
 * How a person's name is rendered in a CRM filter dropdown.
 *
 * Every list page's facet builder used to inline the same expression:
 *
 *     [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || String(u.id)
 *
 * `filter(Boolean)` only drops null/undefined/"" — a `last_name` of the literal
 * string `"-"` is truthy, so it survived. Production's Salesperson dropdown on
 * /crm/customers therefore listed `dbrenner -`, `dtrusty -`, `info -`,
 * `jsmits -`, `j.smits.home -` and `tanand -` alongside the real names: six
 * accounts created with a placeholder surname. The requirement is "name surname
 * only", so the placeholder must not reach the screen.
 *
 * This CLEANS THE LABEL ONLY — it never removes a person from a list. The
 * server still accepts those ids as filter values, and hiding the option would
 * make their assignments unfilterable.
 *
 * The six copies now all call here; `person-label.test.ts` pins the behaviour,
 * including the exact production strings above.
 */

/**
 * The shape every CRM facet builder needs off a user row. Deliberately looser
 * than `hooks/useUsers.ts`'s `User` (whose name fields are non-optional
 * `string`) so a test fixture or a partially-populated row still type-checks —
 * a real `User` is assignable to it. `full_name` and `email` are what the live
 * hook additionally supplies, and they are the fallback ladder below.
 */
export interface PersonLike {
  id: number | string;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  email?: string | null;
}

/**
 * A name part that carries no information: blank, or punctuation standing in
 * for "unknown". Kept deliberately short — the observed production value is
 * `"-"`, and the neighbours (`"--"`, an em/en dash a paste turned into, a bare
 * underscore) are the same gesture. Anything else is treated as a real name;
 * guessing at a longer denylist would start eating surnames.
 */
const PLACEHOLDER = /^[-–—_]+$/;

/** A name part, or "" if it is absent or a placeholder. */
function namePart(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim();
  return trimmed === "" || PLACEHOLDER.test(trimmed) ? "" : trimmed;
}

/** Join name parts, dropping placeholders and collapsing runs of whitespace. */
function cleanName(...parts: (string | null | undefined)[]): string {
  return parts
    .flatMap((part) => (part ?? "").split(/\s+/))
    .map(namePart)
    .filter((part) => part !== "")
    .join(" ");
}

/**
 * The display label for a person: their name and surname, never a dangling
 * placeholder, and never empty.
 *
 * Falls back, in order, to `full_name` (cleaned the same way), then the
 * local-part of `email`, then the id. Email beats the id because these rows are
 * exactly the ones with no usable name, and their `first_name` already IS an
 * email local-part (`dbrenner`, `tanand`) — so the fallback reads like the rest
 * of the list instead of putting a bare number in a dropdown of names.
 */
export function personLabel(person: PersonLike): string {
  const name = cleanName(person.first_name, person.last_name);
  if (name !== "") return name;

  const full = cleanName(person.full_name);
  if (full !== "") return full;

  const localPart = namePart((person.email ?? "").split("@")[0]);
  if (localPart !== "") return localPart;

  return String(person.id);
}
